/**
 * Certificate Expiry Banner
 * Visar en varning för systemadmin när bevakade certifikat närmar sig utgång eller har gått ut.
 * Visas endast för global admin och kan döljas under sessionen.
 */

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ExclamationTriangleIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { useAuth } from '@/store/authStore';
import { useTranslation } from '@/store/languageStore';
import { ROUTES } from '@/constants/routes';
import { isGlobalAdminUser } from '@/utils/isGlobalAdminUser';
import { certificateMonitorService } from '@/services/sysadmin/certificateMonitorService';

const DISMISS_KEY = 'dqfleet.certBannerDismissed';

export const CertificateExpiryBanner = () => {
  const { user } = useAuth();
  const { t } = useTranslation();
  const [expiring, setExpiring] = useState(0);
  const [expired, setExpired] = useState(0);
  const [dismissed, setDismissed] = useState(false);

  const isGlobalAdmin = isGlobalAdminUser(user);

  useEffect(() => {
    if (!isGlobalAdmin) return;
    try {
      if (sessionStorage.getItem(DISMISS_KEY) === '1') setDismissed(true);
    } catch {
      /* ignore */
    }
    let active = true;
    certificateMonitorService
      .getSummary()
      .then((s) => {
        if (!active) return;
        setExpiring(s.expiring);
        setExpired(s.expired);
      })
      .catch(() => {
        /* tyst – banner är icke-kritisk */
      });
    return () => {
      active = false;
    };
  }, [isGlobalAdmin]);

  if (!isGlobalAdmin || dismissed) return null;
  if (expiring === 0 && expired === 0) return null;

  const urgent = expired > 0;

  const handleDismiss = () => {
    setDismissed(true);
    try {
      sessionStorage.setItem(DISMISS_KEY, '1');
    } catch {
      /* ignore */
    }
  };

  return (
    <div className={`${urgent ? 'bg-red-50 border-red-400' : 'bg-amber-50 border-amber-400'} border-l-4 p-3 mb-4 rounded`}>
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <ExclamationTriangleIcon className={`h-5 w-5 ${urgent ? 'text-red-400' : 'text-amber-400'}`} />
          <p className={`text-sm font-medium ${urgent ? 'text-red-700' : 'text-amber-700'}`}>
            {t('sysAdmin.certMonitor.bannerSummary', { expired, expiring })}{' '}
            <Link to={ROUTES.SYSADMIN_CERTIFICATE_MONITOR} className="underline">
              {t('sysAdmin.certMonitor.bannerLink')}
            </Link>
          </p>
        </div>
        <button onClick={handleDismiss} className="p-1 rounded hover:bg-black/5" aria-label="dismiss">
          <XMarkIcon className={`h-4 w-4 ${urgent ? 'text-red-500' : 'text-amber-500'}`} />
        </button>
      </div>
    </div>
  );
};

export default CertificateExpiryBanner;
